import React, { useState, useEffect } from 'react'
import { Button, TextField, MenuItem, Select, InputLabel, FormControl, Typography, Grid, Card, CardActions, CardContent, Box, Chip, ListItemText, Checkbox, FormControlLabel, Switch } from '@mui/material'
import { useForm, SubmitHandler, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from 'src/store'
import { Department } from 'src/features/departmentsSlice'
import { fetchPositionById, fetchPositions, Position, updatePosition } from 'src/features/positionsSlice'
import RoleChipSelect from './RoleChipSelect'
import SkillsSection from './SkillsSection'
import PositionPointDistribution, { PositionDistributionValue } from './PositionPointDistribution'
import { countries } from 'src/constants/countries'
import { countryCodeMap, getCountryFilterBy } from 'src/constants/countryCodes'
import { seniorityLevels, getSeniorityLevelLabel } from 'src/constants/seniorityLevels'
import { NumericFormatCustom } from '../shared/NumericFormatCustom'
import CreateOptionSelect from '../shared/CreateOptionSelect'
import RichTextField from '../shared/RichTextField'
import CircleIcon from '@mui/icons-material/Circle'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from '../SnackbarProvider'
import axios from 'axios'
import { API_URL } from 'src/config/api'
import { useTranslation } from 'react-i18next'
import i18n from 'src/i18n'
import { normalizeApiLanguage } from 'src/utils/language'
import { dedupeSkills } from './skillUtils'

interface Skill {
  name: string
  label?: string
  matchScore: number
}

interface SelectableItem {
  key: string
  text: string
}

const getSelectableKey = (options: SelectableItem[], value: string): string => {
  if (!value) return ''
  const byKey = options.find((option) => option.key === value)
  if (byKey) return byKey.key
  const byText = options.find((option) => option.text === value)
  if (byText) return byText.key
  const normalizedValue = value.trim().toLowerCase()
  const byTextInsensitive = options.find((option) => option.text.trim().toLowerCase() === normalizedValue)
  return byTextInsensitive ? byTextInsensitive.key : value
}

const getSelectableLabel = (options: SelectableItem[], value: string): string => {
  if (!value) return ''
  return options.find((option) => option.key === value)?.text || value
}

const desiredAttributesSchema = Yup.array()
  .of(
    Yup.object({
      key: Yup.string().required(),
      percentage: Yup.number().required().min(0).max(100)
    })
  )
  .test('total-100', 'Total points must be exactly 100', (value) => {
    const total = (value || []).reduce((sum, item) => sum + (item?.percentage || 0), 0)
    return total === 100
  })
  .required('Please distribute points')

const schema = Yup.object({
  jobTitle: Yup.string().required('Job title is required'),
  departmentId: Yup.string().required('Department is required'),
  unit: Yup.string(),
  team: Yup.string(),
  seniorityLevel: Yup.string().required('Seniority level is required'),
  workType: Yup.string().required('Work type is required'),
  employeeLocation: Yup.string().required('Employee location is required'),
  city: Yup.string().notRequired(),
  state: Yup.string().notRequired(),
  district: Yup.string().notRequired(),
  employmentType: Yup.string().required('Employment type is required'),
  minSalary: Yup.string().required('Minimum salary is required'),
  maxSalary: Yup.string().required('Maximum salary is required'),
  currency: Yup.string().required('Currency is required'),
  showSalaryToTalent: Yup.boolean().notRequired(),
  visaSponsorship: Yup.string().required('Visa sponsorship is required'),
  urgentHiring: Yup.string().required('Urgent Hiring selection is required'),
  employmentScope: Yup.string().required('Employment scope is required'),
  skills: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().required('Skill name is required'),
        matchScore: Yup.number().required('Match score is required').min(0, 'Match score must be at least 0').max(100, "Match score can't be more than 100")
      })
    )
    .min(1, 'At least one skill is required'),
  aboutUs: Yup.string().notRequired(),
  teamResponsibilities: Yup.string().notRequired(),
  requirements: Yup.string().notRequired(),
  desiredAttributes1: desiredAttributesSchema,
  desiredAttributes2: desiredAttributesSchema,
  whatWeOffer: Yup.string().notRequired(),
  status: Yup.string().notRequired(),
  benefits: Yup.array().notRequired(),
  education: Yup.array().notRequired(),
  physicalConditions: Yup.array().notRequired(),
  workSchedule: Yup.string().notRequired()
})

// Infer type from Yup schema to ensure perfect type alignment
type JobFormInputs = Yup.InferType<typeof schema>

interface Skill {
  name: string
  label?: string
  matchScore: number
  points?: number
}

const JobDetailPage = () => {
  const dispatch = useDispatch<AppDispatch>()

  const { t } = useTranslation()
  const currentLanguage = normalizeApiLanguage(i18n.resolvedLanguage || i18n.language)
  const seniorityLabelLanguage: 'tr' | 'en' = currentLanguage.toLowerCase().startsWith('tr') ? 'tr' : 'en'
  const { id } = useParams<{ id: string }>()
  const { departments, loading } = useSelector((state: RootState) => state.departments)
  const { positions } = useSelector((state: RootState) => state.positions)
  const { token } = useSelector((state: RootState) => state.auth)
  const { openSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const [selectedSkills, setSelectedSkills] = useState<Skill[]>([])
  const [allSkills, setAllSkills] = useState<Skill[]>([])
  const [jobTitleSuggestions, setJobTitleSuggestions] = useState<string[]>([])
  const [positionFetched, setPositionFetched] = useState(false)
  const [physicalConditionsOptions, setPhysicalConditionsOptions] = useState<SelectableItem[]>([])
  const [countryOptions, setCountryOptions] = useState<SelectableItem[]>([])
  const [workingMethodOptions, setWorkingMethodOptions] = useState<SelectableItem[]>([])
  const [typeOfPositionOptions, setTypeOfPositionOptions] = useState<SelectableItem[]>([])
  const [employmentScopeOptions, setEmploymentScopeOptions] = useState<SelectableItem[]>([])
  const [hiringUrgencyOptions, setHiringUrgencyOptions] = useState<SelectableItem[]>([])
  const [workHoursArrangementOptions, setWorkHoursArrangementOptions] = useState<SelectableItem[]>([])
  const [benefitsOptions, setBenefitsOptions] = useState<SelectableItem[]>([])
  const [trainingOpportunitiesOptions, setTrainingOpportunitiesOptions] = useState<SelectableItem[]>([])
  const [desiredAttributes1Options, setDesiredAttributes1Options] = useState<SelectableItem[]>([])
  const [desiredAttributes2Options, setDesiredAttributes2Options] = useState<SelectableItem[]>([])
  const [jobTitleOptions, setJobTitleOptions] = useState<{ title: string }[]>([])
  const [cityOptions, setCityOptions] = useState<{ title: string }[]>([])

  const {
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    control,
    watch,
    trigger,
    reset
  } = useForm<JobFormInputs>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      jobTitle: '',
      departmentId: '',
      unit: '',
      team: '',
      seniorityLevel: '',
      workType: '',
      employeeLocation: '',
      city: '',
      state: '',
      district: '',
      employmentType: '',
      currency: '',
      showSalaryToTalent: true,
      visaSponsorship: '',
      skills: [],
      urgentHiring: '',
      employmentScope: '',
      aboutUs: '',
      teamResponsibilities: '',
      requirements: '',
      desiredAttributes1: [],
      desiredAttributes2: [],
      whatWeOffer: '',
      status: 'Draft',
      benefits: [],
      education: [],
      physicalConditions: [],
      workSchedule: ''
    }
  })

  // NOTE: Departments are already fetched by parent component
  // No need to fetch again here

  // Reset positionFetched when id changes
  useEffect(() => {
    setPositionFetched(false)
  }, [id])

  // Fetch countries from MongoDB
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'countries',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setCountryOptions(items)
      } catch (error) {
        console.error('Failed to fetch countries:', error)
        setCountryOptions(
          countries.map((country) => ({
            key: countryCodeMap[country] || country,
            text: country
          }))
        )
      }
    }
    if (token) {
      fetchCountries()
    }
  }, [token, currentLanguage])

  // Fetch physicalConditions from MongoDB
  useEffect(() => {
    const fetchPhysicalConditions = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'physicalConditions',
            for: 'company',
            language: currentLanguage,
            statement: '',
            filterBy: ''
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        setPhysicalConditionsOptions(response.data)
      } catch (error) {
        console.error('Failed to fetch physicalConditions:', error)
        // Fallback to static options if API fails
        setPhysicalConditionsOptions([
          { key: 'centralofficelocation', text: t('positions.physicalConditionsOptions.centralofficelocation') },
          { key: 'freshcoffeeintheoffice', text: t('positions.physicalConditionsOptions.freshcoffeeintheoffice') },
          { key: 'workspacewithaview', text: t('positions.physicalConditionsOptions.workspacewithaview') },
          { key: 'comfortableseating', text: t('positions.physicalConditionsOptions.comfortableseating') },
          { key: 'latestversionequipment', text: t('positions.physicalConditionsOptions.latestversionequipment') }
        ])
      }
    }
    if (token) {
      fetchPhysicalConditions()
    }
  }, [token, t, currentLanguage])

  // Fetch jobTitles from MongoDB with debounced search
  const handleJobTitleInputChange = async (value: string) => {
    if (value.length > 0) {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzySearch`,
          {
            name: 'jobTitles',
            statement: value
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        // Convert string array to { title: string } format
        setJobTitleOptions(response.data.map((title: string) => ({ title })))
      } catch (error) {
        console.error('Failed to fetch jobTitles:', error)
        // Fallback to static options if API fails
        setJobTitleOptions([
          { title: 'Frontend' },
          { title: 'Backend' },
          { title: 'DevOps' }
        ])
      }
    }
    // Don't clear options when input is empty - let the user see all options
  }

  // Debounced version of handleJobTitleInputChange
  const [debouncedTimeout, setDebouncedTimeout] = React.useState<NodeJS.Timeout | null>(null)

  // Cleanup debounce timeout on unmount
  React.useEffect(() => {
    return () => {
      if (debouncedTimeout) {
        clearTimeout(debouncedTimeout)
      }
    }
  }, [debouncedTimeout])

  const handleJobTitleInputDebounced = (value: string) => {
    if (debouncedTimeout) {
      clearTimeout(debouncedTimeout)
    }
    const newTimeout = setTimeout(() => {
      handleJobTitleInputChange(value)
    }, 300)
    setDebouncedTimeout(newTimeout)
  }

  // Fetch workingMethods from MongoDB
  useEffect(() => {
    const fetchWorkingMethods = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'workingMethods',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setWorkingMethodOptions(items)
      } catch (error) {
        console.error('Failed to fetch workingMethods:', error)
        // Fallback to static options if API fails
        setWorkingMethodOptions([
          { key: 'onsite', text: t('positions.workingMethodsOptions.onsite') },
          { key: 'remote', text: t('positions.workingMethodsOptions.remote') },
          { key: 'hybrid', text: t('positions.workingMethodsOptions.hybrid') }
        ])
      }
    }
    if (token) {
      fetchWorkingMethods()
    }
  }, [token, t, currentLanguage])

  // Fetch typeOfPositions from MongoDB
  useEffect(() => {
    const fetchTypeOfPositions = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'typeOfPositions',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setTypeOfPositionOptions(items)
      } catch (error) {
        console.error('Failed to fetch typeOfPositions:', error)
        // Fallback to static options if API fails
        setTypeOfPositionOptions([
          { key: 'fulltime', text: t('positions.typeOfPositionOptions.fulltime') },
          { key: 'parttime', text: t('positions.typeOfPositionOptions.parttime') },
          { key: 'contractor', text: t('positions.typeOfPositionOptions.contractor') },
          { key: 'temporary', text: t('positions.typeOfPositionOptions.temporary') }
        ])
      }
    }
    if (token) {
      fetchTypeOfPositions()
    }
  }, [token, t, currentLanguage])

  // Fetch employmentScopes from MongoDB
  useEffect(() => {
    const fetchEmploymentScopes = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'employmentScopes',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setEmploymentScopeOptions(items)
      } catch (error) {
        console.error('Failed to fetch employmentScopes:', error)
        // Fallback to static options if API fails
        setEmploymentScopeOptions([
          { key: 'internal', text: t('positions.employmentScopeOptions.internal') },
          { key: 'external', text: t('positions.employmentScopeOptions.external') },
          { key: 'internalandexternal', text: t('positions.employmentScopeOptions.internalandexternal') }
        ])
      }
    }
    if (token) {
      fetchEmploymentScopes()
    }
  }, [token, t, currentLanguage])

  // Fetch hiringUrgencies from MongoDB
  useEffect(() => {
    const fetchHiringUrgencies = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'hiringUrgencies',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setHiringUrgencyOptions(items)
      } catch (error) {
        console.error('Failed to fetch hiringUrgencies:', error)
        setHiringUrgencyOptions([
          { key: 'veryurgent', text: t('positions.hiringUrgencyOptions.veryurgent') },
          { key: 'norush', text: t('positions.hiringUrgencyOptions.norush') },
          { key: 'always', text: t('positions.hiringUrgencyOptions.always') }
        ])
      }
    }
    if (token) {
      fetchHiringUrgencies()
    }
  }, [token, t, currentLanguage])

  // Fetch workhoursArrangements from MongoDB
  useEffect(() => {
    const fetchWorkHoursArrangements = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'workhoursArrangements',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setWorkHoursArrangementOptions(items)
      } catch (error) {
        console.error('Failed to fetch workhoursArrangements:', error)
        setWorkHoursArrangementOptions([
          { key: 'regular', text: t('positions.workingHoursArrangement.regular') },
          { key: 'flexible', text: t('positions.workingHoursArrangement.flexible') },
          { key: 'intensive', text: t('positions.workingHoursArrangement.intensive') },
          { key: 'breakfocused', text: t('positions.workingHoursArrangement.breakfocused') }
        ])
      }
    }
    if (token) {
      fetchWorkHoursArrangements()
    }
  }, [token, t, currentLanguage])

  // Fetch benefits from MongoDB
  useEffect(() => {
    const fetchBenefits = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'benefits',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setBenefitsOptions(items)
      } catch (error) {
        console.error('Failed to fetch benefits:', error)
        setBenefitsOptions([
          { key: 'performancebonus', text: t('positions.benefitsOptions.performancebonus') },
          { key: 'stockoptions', text: t('positions.benefitsOptions.stockoptions') },
          { key: 'privatehealthinsurance', text: t('positions.benefitsOptions.privatehealthinsurance') },
          { key: 'educationsupport', text: t('positions.benefitsOptions.educationsupport') },
          { key: 'overtimepay', text: t('positions.benefitsOptions.overtimepay') },
          { key: 'employeesupportservices', text: t('positions.benefitsOptions.employeesupportservices') },
          { key: 'extendedannualleave', text: t('positions.benefitsOptions.extendedannualleave') },
          { key: 'transportationassistance', text: t('positions.benefitsOptions.transportationassistance') },
          { key: 'parentalleave', text: t('positions.benefitsOptions.parentalleave') },
          { key: 'remoteworksupportbudget', text: t('positions.benefitsOptions.remoteworksupportbudget') }
        ])
      }
    }
    if (token) {
      fetchBenefits()
    }
  }, [token, t, currentLanguage])

  // Fetch trainingOpportunities from MongoDB
  useEffect(() => {
    const fetchTrainingOpportunities = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'trainingOpportunities',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setTrainingOpportunitiesOptions(items)
      } catch (error) {
        console.error('Failed to fetch trainingOpportunities:', error)
        setTrainingOpportunitiesOptions([
          { key: 'incompanytraining', text: t('positions.trainingOpportunitiesOptions.incompanytraining') },
          { key: 'personaldevelopmenttraining', text: t('positions.trainingOpportunitiesOptions.personaldevelopmenttraining') },
          { key: 'leadershiptraining', text: t('positions.trainingOpportunitiesOptions.leadershiptraining') },
          { key: 'certificationsupport', text: t('positions.trainingOpportunitiesOptions.certificationsupport') },
          { key: 'masterandphdleavesupport', text: t('positions.trainingOpportunitiesOptions.masterandphdleavesupport') },
          { key: 'masterandphdtuitionsupport', text: t('positions.trainingOpportunitiesOptions.masterandphdtuitionsupport') }
        ])
      }
    }
    if (token) {
      fetchTrainingOpportunities()
    }
  }, [token, t, currentLanguage])

  // Fetch desiredAttributes1 and desiredAttributes2 from MongoDB
  useEffect(() => {
    const fetchDesiredAttributes = async () => {
      const fetchOne = async (name: string) => {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name,
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )

        return response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
      }

      try {
        const [attributes1, attributes2] = await Promise.all([
          fetchOne('desiredAttributes1'),
          fetchOne('desiredAttributes2')
        ])
        setDesiredAttributes1Options(attributes1)
        setDesiredAttributes2Options(attributes2)
      } catch (error) {
        console.error('Failed to fetch desiredAttributes:', error)
        setDesiredAttributes1Options([])
        setDesiredAttributes2Options([])
      }
    }

    if (token) {
      fetchDesiredAttributes()
    }
  }, [token, currentLanguage])

  useEffect(() => {
    if (!id || positionFetched || !token) {
      return
    }

    const loadPosition = async () => {
      let existingPosition = positions.find((p) => p.id === id)

      // Refresh/direct URL case: positions list may be empty after reload.
      if (!existingPosition) {
        const listAction = await dispatch(fetchPositions())
        if (fetchPositions.fulfilled.match(listAction)) {
          const fetchedPositions = listAction.payload as Position[]
          existingPosition = fetchedPositions.find((p) => p.id === id)
        }
      }

      if (!existingPosition) {
        openSnackbar('Position not found. Redirecting to positions page.', 'error')
        navigate('/positions')
        return
      }

      const deptId = existingPosition.departmentId || existingPosition.department
      if (!deptId) {
        openSnackbar('Position department not found. Redirecting to positions page.', 'error')
        navigate('/positions')
        return
      }

      const action = await dispatch(fetchPositionById({ departmentId: deptId, positionId: id }))
      if (!fetchPositionById.fulfilled.match(action) || !action.payload) {
        openSnackbar('Failed to fetch position details.', 'error')
        return
      }

      const position = action.payload as Position
      reset({
        jobTitle: position.jobTitle ?? '',
        departmentId: position.department,
        unit: position.unit ?? '',
        team: position.team ?? '',
        seniorityLevel: position.seniorityLevel ?? '',
        workType: position.workType ?? undefined,
        employeeLocation: position.employeeLocation ?? undefined,
        city: position.city ?? undefined,
        state: position.state ?? undefined,
        district: position.district ?? undefined,
        employmentType: position.employmentType ?? undefined,
        minSalary: position.minSalary ?? '',
        maxSalary: position.maxSalary ?? '',
        currency: position.currency ?? '',
        showSalaryToTalent: position.showSalaryToTalent ?? true,
        visaSponsorship: position.visaSponsorship ?? undefined,
        skills: (position.skills || []).map((skill) =>
          typeof skill === 'string'
            ? { name: skill, label: skill, matchScore: 0, points: 0 }
            : { name: skill.name, label: skill.name, matchScore: skill.matchScore ?? 0, points: skill.matchScore ?? 0 }
        ),
        urgentHiring: position.urgentHiring ?? undefined,
        employmentScope: position.employmentScope ?? '',
        aboutUs: position.aboutUs ?? '',
        teamResponsibilities: position.teamResponsibilities ?? '',
        requirements: position.requirements ?? '',
        desiredAttributes1: position.desiredAttributes1 ?? [],
        desiredAttributes2: position.desiredAttributes2 ?? [],
        whatWeOffer: position.whatWeOffer ?? '',
        status: position.status ?? '',
        benefits: position.benefits ?? [],
        education: position.education ?? [],
        physicalConditions: position.physicalConditions ?? [],
        workSchedule: position.workSchedule ?? undefined
      })
      setSelectedSkills(
        dedupeSkills((position.skills || []).map((skill) =>
          typeof skill === 'string'
            ? { name: skill, label: skill, matchScore: 0, points: 0 }
            : { name: skill.name, label: skill.name, matchScore: skill.matchScore ?? 0, points: skill.matchScore ?? 0 }
        ))
      )
      setPositionFetched(true)
    }

    loadPosition()
  }, [dispatch, id, reset, positionFetched, navigate, openSnackbar, positions, token])

  const handleAddSkill = (newSkills: Skill[]) => {
    const uniqueSkills = dedupeSkills(newSkills)

    setSelectedSkills(uniqueSkills)
    setValue('skills', uniqueSkills, { shouldDirty: true, shouldValidate: true })
  }

  const watchedEmployeeLocation = watch('employeeLocation')
  const watchedCity = watch('city')
  const watchedState = watch('state')
  const watchedJobTitle = watch('jobTitle')
  const watchedBenefits = watch('benefits')
  const watchedEducation = watch('education')
  const watchedDesiredAttributes1 = watch('desiredAttributes1')
  const watchedDesiredAttributes2 = watch('desiredAttributes2')

  const normalizeDesiredAttributes = (
    options: SelectableItem[],
    values?: PositionDistributionValue[]
  ): PositionDistributionValue[] => {
    return options.map((option) => ({
      key: option.key,
      percentage: Math.max(
        0,
        Math.min(
          100,
          Math.round(values?.find((item) => item.key === option.key)?.percentage ?? 0)
        )
      )
    }))
  }

  const prevEmployeeLocationRef = React.useRef<string | null>(null)
  const [citySearchDebounce, setCitySearchDebounce] = React.useState<NodeJS.Timeout | null>(null)
  const jobTitleSkillsDebounceRef = React.useRef<NodeJS.Timeout | null>(null)

  // Fetch skills from MongoDB with debounced search
  const handleSkillSearch = async (value: string) => {
    if (value.length > 0) {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'skills',
            statement: value
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        // Convert Item array to Skill format with default matchScore
        const skills = response.data.map((item: { key: string; text: string }) => ({
          name: item.key,
          label: item.text,
          matchScore: 0
        }))
        setAllSkills(dedupeSkills(skills))
      } catch (error) {
        console.error('Failed to fetch skills:', error)
        setAllSkills([])
      }
    }
  }

  // Debounced version of handleSkillSearch
  const [skillSearchDebounce, setSkillSearchDebounce] = React.useState<NodeJS.Timeout | null>(null)

  // Cleanup debounce timeout on unmount
  React.useEffect(() => {
    return () => {
      if (skillSearchDebounce) {
        clearTimeout(skillSearchDebounce)
      }
    }
  }, [skillSearchDebounce])

  const handleSkillSearchDebounced = (value: string) => {
    if (skillSearchDebounce) {
      clearTimeout(skillSearchDebounce)
    }
    const newTimeout = setTimeout(() => {
      handleSkillSearch(value)
    }, 300)
    setSkillSearchDebounce(newTimeout)
  }

  // Cleanup city debounce timeout on unmount
  React.useEffect(() => {
    return () => {
      if (citySearchDebounce) {
        clearTimeout(citySearchDebounce)
      }
    }
  }, [citySearchDebounce])

  // Cleanup job title skills debounce timeout on unmount
  React.useEffect(() => {
    return () => {
      if (jobTitleSkillsDebounceRef.current) {
        clearTimeout(jobTitleSkillsDebounceRef.current)
      }
    }
  }, [])

  const fetchCities = async (value: string) => {
    const filterBy = getCountryFilterBy(watchedEmployeeLocation)
    try {
      const response = await axios.post(
        `${API_URL}/fuzzy/fuzzyWithItemSearch`,
        {
          name: 'cities',
          statement: value,
          for: 'company',
          language: currentLanguage,
          filterBy
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      const items = response.data.map((item: { key: string; text: string }) => ({
        title: item.text
      }))
      setCityOptions(items)
    } catch (error) {
      console.error('Failed to fetch cities:', error)
      setCityOptions([])
    }
  }

  const handleCityInputDebounced = (value: string) => {
    if (!token || !watchedEmployeeLocation) return
    if (citySearchDebounce) {
      clearTimeout(citySearchDebounce)
    }
    const newTimeout = setTimeout(() => {
      fetchCities(value)
    }, 300)
    setCitySearchDebounce(newTimeout)
  }

  useEffect(() => {
    if (!token) return
    if (prevEmployeeLocationRef.current === null) {
      prevEmployeeLocationRef.current = watchedEmployeeLocation || ''
      if (watchedEmployeeLocation) {
        fetchCities('')
      }
      return
    }
    if (prevEmployeeLocationRef.current !== watchedEmployeeLocation) {
      prevEmployeeLocationRef.current = watchedEmployeeLocation || ''
      setValue('city', '')
      setValue('state', '')
      setValue('district', '')
      setCityOptions([])
      if (watchedEmployeeLocation) {
        fetchCities('')
      }
    }
  }, [token, watchedEmployeeLocation, setValue])

  const fetchSkillsForJobTitle = async (value: string) => {
    if (!value.trim()) {
      setAllSkills([])
      return
    }
    try {
      const response = await axios.post(
        `${API_URL}/fuzzy/fuzzyWithItemSearch`,
        {
          name: 'skills',
          statement: value,
          for: 'company',
          language: currentLanguage,
          filterBy: ''
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      const skills = response.data.map((item: { key: string; text: string }) => ({
        name: item.key,
        label: item.text,
        matchScore: 0
      }))
      setAllSkills(dedupeSkills(skills))
    } catch (error) {
      console.error('Failed to fetch skills for job title:', error)
    }
  }

  useEffect(() => {
    if (!token) return
    if (!watchedJobTitle || watchedJobTitle.trim().length < 2) {
      setAllSkills([])
      return
    }
    if (jobTitleSkillsDebounceRef.current) {
      clearTimeout(jobTitleSkillsDebounceRef.current)
    }
    const newTimeout = setTimeout(() => {
      fetchSkillsForJobTitle(watchedJobTitle)
    }, 300)
    jobTitleSkillsDebounceRef.current = newTimeout
  }, [token, watchedJobTitle])

  useEffect(() => {
    if (!benefitsOptions.length || !watchedBenefits?.length) return
    const normalized = watchedBenefits.map((value) => getSelectableKey(benefitsOptions, value))
    if (JSON.stringify(normalized) !== JSON.stringify(watchedBenefits)) {
      setValue('benefits', normalized, { shouldDirty: false, shouldValidate: false })
    }
  }, [benefitsOptions, watchedBenefits, setValue])

  useEffect(() => {
    if (!trainingOpportunitiesOptions.length || !watchedEducation?.length) return
    const normalized = watchedEducation.map((value) => getSelectableKey(trainingOpportunitiesOptions, value))
    if (JSON.stringify(normalized) !== JSON.stringify(watchedEducation)) {
      setValue('education', normalized, { shouldDirty: false, shouldValidate: false })
    }
  }, [trainingOpportunitiesOptions, watchedEducation, setValue])

  useEffect(() => {
    if (!desiredAttributes1Options.length) return
    const normalized = normalizeDesiredAttributes(desiredAttributes1Options, watchedDesiredAttributes1 as PositionDistributionValue[] | undefined)
    if (JSON.stringify(normalized) !== JSON.stringify(watchedDesiredAttributes1 || [])) {
      setValue('desiredAttributes1', normalized, { shouldDirty: false, shouldValidate: false })
    }
  }, [desiredAttributes1Options, watchedDesiredAttributes1, setValue])

  useEffect(() => {
    if (!desiredAttributes2Options.length) return
    const normalized = normalizeDesiredAttributes(desiredAttributes2Options, watchedDesiredAttributes2 as PositionDistributionValue[] | undefined)
    if (JSON.stringify(normalized) !== JSON.stringify(watchedDesiredAttributes2 || [])) {
      setValue('desiredAttributes2', normalized, { shouldDirty: false, shouldValidate: false })
    }
  }, [desiredAttributes2Options, watchedDesiredAttributes2, setValue])

  useEffect(() => {
    if (!countryOptions.length || !watchedEmployeeLocation) return
    const normalized = getSelectableKey(countryOptions, watchedEmployeeLocation)
    if (normalized !== watchedEmployeeLocation) {
      setValue('employeeLocation', normalized, { shouldDirty: false, shouldValidate: false })
    }
  }, [countryOptions, watchedEmployeeLocation, setValue])

  // Reset selected skills when job title changes
  useEffect(() => {
    setSelectedSkills([])
    setValue('skills', [])
  }, [setValue, watchedJobTitle])

  const onSubmit: SubmitHandler<JobFormInputs> = (data) => {
    if (data.skills && id) {
      const normalizedEmploymentType = getSelectableKey(typeOfPositionOptions, data.employmentType)
      const normalizedWorkType = getSelectableKey(workingMethodOptions, data.workType)
      const normalizedEmploymentScope = getSelectableKey(employmentScopeOptions, data.employmentScope)
      const normalizedUrgentHiring = getSelectableKey(hiringUrgencyOptions, data.urgentHiring)
      const normalizedWorkSchedule = getSelectableKey(workHoursArrangementOptions, data.workSchedule || '')
      const normalizedBenefits = (data.benefits ?? []).map((value) => getSelectableKey(benefitsOptions, value))
      const normalizedEducation = (data.education ?? []).map((value) => getSelectableKey(trainingOpportunitiesOptions, value))
      const normalizedEmployeeLocation = getSelectableKey(countryOptions, data.employeeLocation)
      const payload = {
        position: {
          id,
          ...data,
          employeeLocation: normalizedEmployeeLocation,
          workType: normalizedWorkType,
          employmentType: normalizedEmploymentType,
          employmentScope: normalizedEmploymentScope,
          urgentHiring: normalizedUrgentHiring,
          workSchedule: normalizedWorkSchedule,
          showSalaryToTalent: data.showSalaryToTalent ?? true,
          skills: data.skills?.map((x) => ({ name: x.name, matchScore: x.matchScore })),
          benefits: normalizedBenefits,
          education: normalizedEducation,
          physicalConditions: data.physicalConditions ?? [],
          desiredAttributes1: data.desiredAttributes1 || [],
          desiredAttributes2: data.desiredAttributes2 || [],
          department: data.departmentId
        },
        departmentId: data.departmentId
      }
      console.log('🚀 Updating position with payload:', payload)
      dispatch(updatePosition(payload)).then(() => {
        openSnackbar(t('positions.messages.updated'), 'success')
        navigate('/positions')
      })
    }
  }

  return (
    <Card
      sx={{
        bgcolor: 'white',
        position: 'relative',
        width: '100%'
      }}>
      <CardContent sx={{ p: 3 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid
            container
            justifyContent={'space-between'}>
            <Grid
              container
              spacing={3}
              xs={9}>
              <Grid
                item
                xs={12}
                sm={4}>
                <FormControl
                  fullWidth
                  sx={{ mt: 2 }}>
                  <InputLabel id="department-label">{t('positions.form.department')}</InputLabel>
                  <Controller
                    name="departmentId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="department-label"
                        label={t('positions.form.department')}
                        error={!!errors.departmentId}>
                        {loading ? (
                          <MenuItem value="">{t('common.loading')}</MenuItem>
                        ) : (
                          departments.map((department: Department) => (
                            <MenuItem
                              key={department.id}
                              value={department.id}>
                              {department.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    )}
                  />
                  {errors.departmentId && (
                    <Typography
                      variant="caption"
                      color="error">
                      {errors.departmentId.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}>
                <Controller
                  name="unit"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      sx={{ mt: 2 }}
                      label={t('positions.form.unit')}
                      {...field}
                      error={!!errors.unit}
                      helperText={errors.unit?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}>
                <Controller
                  name="team"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      sx={{ mt: 2 }}
                      label={t('positions.form.team')}
                      {...field}
                      error={!!errors.team}
                      helperText={errors.team?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={7}>
                <Controller
                  name="jobTitle"
                  control={control}
                  render={({ field }) => (
                    <CreateOptionSelect
                      options={jobTitleOptions}
                      value={field.value ? { title: field.value } : null}
                      inputValue={field.value ?? ''}
                      onInputChange={(inputValue) => {
                        field.onChange(inputValue)
                        handleJobTitleInputDebounced(inputValue)
                      }}
                      onChange={(newValue) => {
                        field.onChange(newValue?.title ?? '')
                      }}
                      name={field.name}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      fullWidth
                      label={t('positions.form.jobTitle')}
                      error={!!errors.jobTitle}
                      helperText={errors.jobTitle?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={5}>
                <FormControl fullWidth>
                  <InputLabel id="seniority-level-label">{t('positions.form.seniorityLevel')}</InputLabel>
                  <Controller
                    name="seniorityLevel"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="seniority-level-label"
                        label={t('positions.form.seniorityLevel')}
                        error={!!errors.seniorityLevel}>
                        {seniorityLevels.map((level) => (
                          <MenuItem
                            key={level}
                            value={level}>
                            {getSeniorityLevelLabel(level, seniorityLabelLanguage)}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.seniorityLevel && (
                    <Typography
                      variant="caption"
                      color="error">
                      {errors.seniorityLevel.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                sm={watchedCity ? 3 : watchedEmployeeLocation ? 6 : 9}>
                <FormControl fullWidth>
                  <InputLabel id="employee-location-label">{t('positions.form.workLocation')}</InputLabel>
                  <Controller
                    name="employeeLocation"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="employee-location-label"
                        label={t('positions.form.workLocation')}>
                        {countryOptions.map((country) => (
                          <MenuItem
                            key={country.key}
                            value={country.key}>
                            {country.text}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.employeeLocation && (
                    <Typography
                      variant="caption"
                      color="error">
                      {errors.employeeLocation.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {watchedEmployeeLocation && (
                <Grid
                  item
                  xs={12}
                  sm={3}>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <CreateOptionSelect
                        {...field}
                        options={cityOptions}
                        value={{ title: field.value || '' }}
                        onInputChange={(value) => {
                          setValue('city', value, { shouldDirty: true })
                          handleCityInputDebounced(value)
                        }}
                        onChange={(value) => {
                          field.onChange(value?.title ?? '')
                          setValue('city', value?.title ?? '', {
                            shouldDirty: true
                          })
                        }}
                        fullWidth
                        label={t('positions.form.city')}
                        error={!!errors.city}
                        helperText={errors.city?.message}
                      />
                    )}
                  />
                </Grid>
              )}

              {watchedCity && (
                <Grid
                  item
                  xs={12}
                  sm={3}>
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label={t('positions.form.state')}
                        error={!!errors.state}
                        helperText={errors.state?.message}
                      />
                    )}
                  />
                </Grid>
              )}

              {watchedState && (
                <Grid
                  item
                  xs={12}
                  sm={3}>
                  <Controller
                    name="district"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label={t('positions.form.district')}
                        error={!!errors.district}
                        helperText={errors.district?.message}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid
                item
                xs={12}
                sm={6}>
                <Controller
                  name="workType"
                  control={control}
                  render={({ field }) => (
                    <CreateOptionSelect
                      options={workingMethodOptions.map((x) => ({
                        title: x.text
                      }))}
                      value={{ title: getSelectableLabel(workingMethodOptions, field.value || '') }}
                      onChange={(value) => {
                        setValue('workType', getSelectableKey(workingMethodOptions, value?.title ?? ''), {
                          shouldDirty: true
                        })
                        trigger('workType')
                      }}
                      fullWidth
                      label={t('positions.form.workType')}
                      error={!!errors.workType}
                      helperText={errors.workType?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}>
                <Controller
                  name="employmentType"
                  control={control}
                  render={({ field }) => (
                    <CreateOptionSelect
                      options={typeOfPositionOptions.map((x) => ({
                        title: x.text
                      }))}
                      value={{ title: getSelectableLabel(typeOfPositionOptions, field.value || '') }}
                      onChange={(value) => {
                        setValue('employmentType', getSelectableKey(typeOfPositionOptions, value?.title ?? ''), {
                          shouldDirty: true
                        })
                        trigger('employmentType')
                      }}
                      fullWidth
                      label={t('positions.form.employmentType')}
                      error={!!errors.employmentType}
                      helperText={errors.employmentType?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="visa-sponsorship-label">{t('positions.form.visaSponsorship')}</InputLabel>
                  <Controller
                    name="visaSponsorship"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="visa-sponsorship-label"
                        label={t('positions.form.visaSponsorship')}>
                        <MenuItem value="Yes">{t('common.yes')}</MenuItem>
                        <MenuItem value="No">{t('common.no')}</MenuItem>
                      </Select>
                    )}
                  />
                  {errors.visaSponsorship && (
                    <Typography
                      variant="caption"
                      color="error">
                      {errors.visaSponsorship.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}>
                <Controller
                  name="urgentHiring"
                  control={control}
                  render={({ field }) => (
                    <CreateOptionSelect
                      options={hiringUrgencyOptions.map((x) => ({
                        title: x.text
                      }))}
                      value={{ title: getSelectableLabel(hiringUrgencyOptions, field.value || '') }}
                      onChange={(value) => {
                        setValue('urgentHiring', getSelectableKey(hiringUrgencyOptions, value?.title ?? ''), {
                          shouldDirty: true
                        })
                        trigger('urgentHiring')
                      }}
                      fullWidth
                      label={t('positions.form.urgentHiring')}
                      error={!!errors.urgentHiring}
                      helperText={errors.urgentHiring?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}>
                <Controller
                  name="employmentScope"
                  control={control}
                  render={({ field }) => (
                    <CreateOptionSelect
                      options={employmentScopeOptions.map((x) => ({
                        title: x.text
                      }))}
                      value={{ title: getSelectableLabel(employmentScopeOptions, field.value || '') }}
                      onChange={(value) => {
                        setValue('employmentScope', getSelectableKey(employmentScopeOptions, value?.title ?? ''), {
                          shouldDirty: true
                        })
                        trigger('employmentScope')
                      }}
                      fullWidth
                      label={t('positions.form.employmentScope')}
                      error={!!errors.employmentScope}
                      helperText={errors.employmentScope?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}>
                <Controller
                  name="minSalary"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      label={t('positions.form.minSalary')}
                      {...field}
                      error={!!errors.minSalary}
                      helperText={errors.minSalary?.message}
                      InputProps={{
                        inputComponent: NumericFormatCustom as any
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}>
                <Controller
                  name="maxSalary"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      label={t('positions.form.maxSalary')}
                      {...field}
                      error={!!errors.maxSalary}
                      helperText={errors.maxSalary?.message}
                      InputProps={{
                        inputComponent: NumericFormatCustom as any
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="currency-label">{t('positions.form.currency')}</InputLabel>
                  <Controller
                    name="currency"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="currency-label"
                        label={t('positions.form.currency')}
                        error={!!errors.currency}>
                        <MenuItem value="try">{t('positions.form.currencyOptions.try')}</MenuItem>
                        <MenuItem value="usd">{t('positions.form.currencyOptions.usd')}</MenuItem>
                        <MenuItem value="eur">{t('positions.form.currencyOptions.eur')}</MenuItem>
                      </Select>
                    )}
                  />
                  {errors.currency && (
                    <Typography
                      variant="caption"
                      color="error">
                      {errors.currency.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}>
                <Controller
                  name="showSalaryToTalent"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!field.value}
                            onChange={(_, checked) => field.onChange(checked)}
                          />
                        }
                        label={t('positions.form.showSalaryToTalent')}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary">
                        {t('positions.form.showSalaryToTalentHelp')}
                      </Typography>
                    </Box>
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <SkillsSection
                  selectedSkills={selectedSkills}
                  handleAddSkill={handleAddSkill}
                  availableSkills={allSkills}
                  isMatched={allSkills.length > 0}
                  onSkillSearch={handleSkillSearchDebounced}
                />
                {errors.skills && (
                  <Typography
                    variant="caption"
                    color="error">
                    {errors.skills.message}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="desiredAttributes1"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <PositionPointDistribution
                      title={t('positions.form.desiredAttributes1')}
                      options={desiredAttributes1Options}
                      values={(field.value as PositionDistributionValue[]) || []}
                      onChange={(next) =>
                        setValue('desiredAttributes1', next, {
                          shouldDirty: true,
                          shouldValidate: true
                        })
                      }
                      helperText={errors.desiredAttributes1?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="desiredAttributes2"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <PositionPointDistribution
                      title={t('positions.form.desiredAttributes2')}
                      options={desiredAttributes2Options}
                      values={(field.value as PositionDistributionValue[]) || []}
                      onChange={(next) =>
                        setValue('desiredAttributes2', next, {
                          shouldDirty: true,
                          shouldValidate: true
                        })
                      }
                      helperText={errors.desiredAttributes2?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <FormControl
                  fullWidth
                  error={!!errors.benefits}>
                  <InputLabel id="benefits-label">{t("positions.form.selectBenefits")}</InputLabel>
                  <Controller
                    name="benefits"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="benefits-label"
                        label={t("positions.form.selectBenefits")}
                        multiple
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((value) => {
                              const option = benefitsOptions.find((opt) => opt.key === value)
                              return (
                                <Chip
                                  key={value}
                                  label={option ? option.text : value}
                                  sx={{ m: 0.5 }}
                                />
                              )
                            })}
                          </Box>
                        )}>
                        {benefitsOptions.map((benefit) => (
                          <MenuItem
                            key={benefit.key}
                            value={benefit.key}>
                            <Checkbox checked={field.value?.includes(benefit.key) ?? false} />
                            <ListItemText primary={benefit.text} />
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.benefits && (
                    <Typography
                      variant="caption"
                      color="error">
                      {errors.benefits.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}>
                <FormControl
                  fullWidth
                  error={!!errors.education}>
                  <InputLabel id="education-label">{t("positions.form.selectTrainingOpportunities")}</InputLabel>
                  <Controller
                    name="education"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="education-label"
                        label={t("positions.form.selectTrainingOpportunities")}
                        multiple
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((value) => {
                              const option = trainingOpportunitiesOptions.find((opt) => opt.key === value)
                              return (
                                <Chip
                                  key={value}
                                  label={option ? option.text : value}
                                  sx={{ m: 0.5 }}
                                />
                              )
                            })}
                          </Box>
                        )}>
                        {trainingOpportunitiesOptions.map((education) => (
                          <MenuItem
                            key={education.key}
                            value={education.key}>
                            <Checkbox checked={field.value?.includes(education.key) ?? false} />
                            <ListItemText primary={education.text} />
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}>
                <FormControl
                  fullWidth
                  error={!!errors.physicalConditions}>
                  <InputLabel id="physical-conditions-label">{t("positions.form.selectPhysicalConditions")}</InputLabel>
                  <Controller
                    name="physicalConditions"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="physical-conditions-label"
                        label={t("positions.form.selectPhysicalConditions")}
                        multiple
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((value) => {
                              const option = physicalConditionsOptions.find(opt => opt.key === value)
                              return (
                                <Chip
                                  key={value}
                                  label={option ? option.text : value}
                                  sx={{ m: 0.5 }}
                                />
                              )
                            })}
                          </Box>
                        )}>
                        {physicalConditionsOptions.map((option) => (
                          <MenuItem
                            key={option.key}
                            value={option.key}>
                            <Checkbox checked={field.value?.includes(option.key) ?? false} />
                            <ListItemText primary={option.text} />
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}>
                <Controller
                  name="workSchedule"
                  control={control}
                  render={({ field }) => (
                    <CreateOptionSelect
                      options={workHoursArrangementOptions.map((x) => ({
                        title: x.text
                      }))}
                      value={{ title: getSelectableLabel(workHoursArrangementOptions, field.value || '') }}
                      onChange={(value) => {
                        setValue('workSchedule', getSelectableKey(workHoursArrangementOptions, value?.title ?? ''), {
                          shouldDirty: true
                        })
                        trigger('workSchedule')
                      }}
                      fullWidth
                      label={t("positions.form.selectWorkSchedule")}
                      error={!!errors.workSchedule}
                      helperText={errors.workSchedule?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <Controller
                  name="aboutUs"
                  control={control}
                  render={({ field }) => (
                    <RichTextField
                      fullWidth
                      label={t('positions.form.aboutUs')}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={!!errors.aboutUs}
                      helperText={errors.aboutUs?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <Controller
                  name="teamResponsibilities"
                  control={control}
                  render={({ field }) => (
                    <RichTextField
                      fullWidth
                      label={t('positions.form.teamResponsibilities')}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={!!errors.teamResponsibilities}
                      helperText={errors.teamResponsibilities?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <Controller
                  name="requirements"
                  control={control}
                  render={({ field }) => (
                    <RichTextField
                      fullWidth
                      label={t('positions.form.requirements')}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={!!errors.requirements}
                      helperText={errors.requirements?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <Controller
                  name="whatWeOffer"
                  control={control}
                  render={({ field }) => (
                    <RichTextField
                      fullWidth
                      label={t('positions.form.whatWeOffer')}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={!!errors.whatWeOffer}
                      helperText={errors.whatWeOffer?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={3}
              sx={{ pt: 2 }}>
              <RoleChipSelect />
            </Grid>
          </Grid>
        </form>
      </CardContent>
      <CardActions
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          p: 2,
          boxShadow: 3,
          width: '100%',
          bgcolor: 'white',
          position: 'relative',
          zIndex: 31
        }}>
        <FormControl sx={{ minWidth: 120 }}>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                labelId="status-label"
                defaultValue="Posted"
                error={!!errors.status}
                size="small"
                sx={{ borderRadius: '50px', display: 'flex' }}>
                <MenuItem value="Posted">
                  <CircleIcon sx={{ fontSize: '10px', mr: '7px', color: 'green' }} />
                  {t('positions.statusOptions.posted')}
                </MenuItem>
                <MenuItem value="No Post">
                  <CircleIcon sx={{ fontSize: '10px', mr: '7px', color: 'red' }} />
                  {t('positions.statusOptions.noPost')}
                </MenuItem>
                <MenuItem value="Draft">
                  <CircleIcon sx={{ fontSize: '10px', mr: '7px', color: 'orange' }} />
                  {t('positions.statusOptions.draft')}
                </MenuItem>
                <MenuItem value="Closed">
                  <CircleIcon sx={{ fontSize: '10px', mr: '7px', color: 'gray' }} />
                  {t('positions.statusOptions.closed')}
                </MenuItem>
                <MenuItem value="Archived">
                  <CircleIcon sx={{ fontSize: '10px', mr: '7px', color: 'blue' }} />
                  {t('positions.statusOptions.archived')}
                </MenuItem>
              </Select>
            )}
          />
          {errors.status && (
            <Typography
              variant="caption"
              color="error">
              {errors.status?.message}
            </Typography>
          )}
        </FormControl>
        <Button
          variant="contained"
          sx={{ bgcolor: 'grey' }}
          onClick={() => navigate('/positions')}>
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={!isDirty}>
          {t('common.save')}
        </Button>
      </CardActions>
    </Card>
  )
}

export default JobDetailPage
